/**
 * Générateur — Multiplier une fraction par un nombre entier
 * Attendu ID : 631 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 6 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey au niveau exercice), déclinés en plusieurs formats UI
 * pour un total de 9 entrées registry.
 *
 * Exercices couverts (voir FICHE_PEDAGO_CLUSTER17 §6.1 à §6.6) :
 *
 *   1. `produit_fraction_entier_direct`  → QCM, Saisie. Couple (a/b, n) résultat fractionnaire.
 *   2. `produit_resultat_entier`         → QCM, Saisie. Couple (a/b, n) résultat entier.
 *   3. `produit_resultat_decimal`        → Saisie. Couple (a/b, n) résultat décimal exact.
 *   4. `produit_egalite_trou`            → Texte à trous saisie. Deux trous indépendants.
 *   5. `produit_comparer_a_entier`       → QCM. Comparer a/b × n à n.
 *   6. `produit_astucieux`               → QCM. Choisir la méthode de calcul la plus astucieuse.
 *
 * Règles dures de la fiche (§3, §4) :
 *   - Sous-famille A : résultat fraction, couples calibrés b ∈ {6,9,11,14,26}.
 *   - Sous-famille B : (a×n) multiple de b, résultat entier ∈ [1,80].
 *   - Sous-famille C : (a×n)/b décimal fini (b/pgcd de la forme 2^p×5^q).
 *   - Sous-famille D : comparaison à n, cas propre + cas impropre à parts égales.
 *
 * Distracteurs contextualisés (fiche §4) :
 *   1. Multiplication à deux étages : (a × n)/(b × n)
 *   2. Confusion + et × : a + n/b
 *   3. Oubli de simplification finale
 *   4. Incomplétude (entier laissé en fraction)
 *   5. Virgule décimale mal placée
 *   6. Raisonnement fautif sur la comparaison
 *   7. Méthode astucieuse confondue avec méthode lourde
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, tex,
} from '../../../utils';
import { gcd, simplify, fmtFrac, fmtKatex, fmtItem } from './fraction';

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 631;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DIRECT   = 'Calculer un produit fraction × entier (résultat fractionnaire)';
const L_ENTIER   = 'Calculer un produit fraction × entier (résultat entier)';
const L_DECIMAL  = 'Calculer un produit fraction × entier (résultat décimal)';
const L_TROU     = 'Compléter une égalité fraction × entier';
const L_COMPARE  = 'Comparer un produit fraction × entier à cet entier';
const L_ASTUCE   = 'Choisir la méthode de calcul la plus astucieuse';

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES
// ═══════════════════════════════════════════════════════════

/** Formate le résultat simplifié : entier si dén=1, sinon num/den. */
const fmtResult = (a, b) => {
    const s = simplify(a, b);
    return fmtFrac(s.num, s.den);
};

/** Teste si une fraction a/b simplifiée est un entier. */
const isInteger = (a, b) => simplify(a, b).den === 1;

/**
 * Convertit a/b en décimal exact (string avec virgule) si c'est possible.
 * Retourne null si le développement décimal est infini.
 */
const fractionToDecimalExact = (a, b) => {
    const s = simplify(a, b);
    let num = s.num;
    let den = s.den;
    // Retirer tous les facteurs 2 et 5 du dénominateur
    while (den % 2 === 0) { den /= 2; num *= 5; }
    while (den % 5 === 0) { den /= 5; num *= 2; }
    if (den !== 1) return null; // décimal infini
    // Maintenant a/b = num / (10^k) où k est la puissance introduite
    // Calculons la puissance de 10 : on a multiplié num par 5 chaque fois qu'on
    // retirait un 2, et par 2 chaque fois qu'on retirait un 5. Le dénominateur
    // final est donc 10^(nb2+nb5).
    // Plus simple : recalculer num/10^k en déterminant k via longueur.
    // On peut juste diviser num/b... Mais la méthode plus simple :
    // utiliser String (num / 10^k) avec k = nb total de retraits.
    // Plus simple encore : on retire les facteurs en comptant.
    // Réécrivons proprement :
    let n = s.num;
    let d = s.den;
    let p2 = 0, p5 = 0;
    while (d % 2 === 0) { d /= 2; p2++; }
    while (d % 5 === 0) { d /= 5; p5++; }
    if (d !== 1) return null;
    // a/b = n / (2^p2 × 5^p5)
    // On veut ramener au dénominateur 10^max(p2,p5)
    const k = Math.max(p2, p5);
    const factor = Math.pow(2, k - p2) * Math.pow(5, k - p5);
    const numFinal = n * factor;
    const denFinal = Math.pow(10, k);
    // Maintenant numFinal / denFinal est le résultat exact
    if (denFinal === 1) return String(numFinal);
    const numStr = String(numFinal).padStart(k + 1, '0');
    const intPart = numStr.slice(0, numStr.length - k);
    const fracPart = numStr.slice(numStr.length - k).replace(/0+$/, '');
    return fracPart ? `${intPart},${fracPart}` : intPart;
};

// ═══════════════════════════════════════════════════════════
// POOL A — Résultat fractionnaire (sous-famille A)
// ═══════════════════════════════════════════════════════════

/** Couples [a, b, n] tels que (a×n)/b soit une fraction non-entière, num ≤ 200. */
const POOL_A = [
    // b = 6
    [5, 6, 13],  // 65/6
    [1, 6, 5],   // 5/6
    [5, 6, 7],   // 35/6
    [1, 6, 11],  // 11/6
    [5, 6, 11],  // 55/6
    [7, 6, 5],   // 35/6
    [11, 6, 3],  // 33/6 = 11/2
    // b = 9
    [2, 9, 21],  // 42/9 = 14/3
    [1, 9, 5],   // 5/9
    [2, 9, 7],   // 14/9
    [4, 9, 5],   // 20/9
    [4, 9, 7],   // 28/9
    [8, 9, 5],   // 40/9
    [7, 9, 4],   // 28/9
    // b = 11
    [12, 11, 9], // 108/11
    [3, 11, 7],  // 21/11
    [5, 11, 13], // 65/11
    [7, 11, 8],  // 56/11
    [10, 11, 13],// 130/11
    [6, 11, 5],  // 30/11
    [9, 11, 2],  // 18/11
    // b = 14
    [5, 14, 12], // 60/14 = 30/7
    [3, 14, 5],  // 15/14
    [5, 14, 9],  // 45/14
    [11, 14, 3], // 33/14
    [9, 14, 5],  // 45/14
    // b = 26
    [15, 26, 6], // 90/26 = 45/13
    [7, 26, 9],  // 63/26
    [11, 26, 3], // 33/26
    [13, 26, 5], // 65/26
    [17, 26, 7], // 119/26
];

// Validation runtime au chargement : on vérifie que chaque entrée de POOL_A
// donne un résultat non-entier après simplification et num ≤ 200.
for (const [a, b, n] of POOL_A) {
    const numRes = a * n;
    if (numRes > 200) throw new Error(`POOL_A: numérateur > 200 pour (${a},${b},${n})`);
    if (isInteger(numRes, b)) throw new Error(`POOL_A: résultat entier pour (${a},${b},${n})`);
}

const pickPoolA = () => {
    const [a, b, n] = randomChoice(POOL_A);
    return { a, b, n };
};

// ═══════════════════════════════════════════════════════════
// POOL B — Résultat entier (sous-famille B)
// ═══════════════════════════════════════════════════════════

/** Couples [a, b, n, resultatEntier] où (a×n) est multiple de b. */
const POOL_B = [
    // b = 2
    [3, 2, 26, 39],
    [5, 2, 6, 15],
    [3, 2, 8, 12],
    [7, 2, 10, 35],
    [9, 2, 4, 18],
    // b = 3
    [2, 3, 33, 22],
    [2, 3, 9, 6],
    [5, 3, 6, 10],
    [7, 3, 12, 28],
    [4, 3, 9, 12],
    [2, 3, 12, 8],
    [5, 3, 9, 15],
    // b = 4
    [3, 4, 40, 30],
    [3, 4, 16, 12],
    [5, 4, 8, 10],
    [7, 4, 12, 21],
    [3, 4, 20, 15],
    [5, 4, 12, 15],
    [7, 4, 8, 14],
    // b = 5
    [8, 5, 15, 24],
    [3, 5, 10, 6],
    [4, 5, 25, 20],
    [3, 5, 20, 12],
    [7, 5, 15, 21],
    [6, 5, 10, 12],
    // b = 6
    [5, 6, 12, 10],
    [5, 6, 18, 15],
    [7, 6, 12, 14],
    // b = 7
    [2, 7, 21, 6],
    [3, 7, 14, 6],
    [5, 7, 14, 10],
    [4, 7, 21, 12],
    // b = 10
    [20, 10, 9, 18],
    [3, 10, 20, 6],
    [7, 10, 20, 14],
];

// Validation runtime
for (const [a, b, n, res] of POOL_B) {
    if ((a * n) % b !== 0) throw new Error(`POOL_B: (${a},${b},${n}) non-entier`);
    if ((a * n) / b !== res) throw new Error(`POOL_B: résultat faux pour (${a},${b},${n})`);
    if (res < 1 || res > 80) throw new Error(`POOL_B: résultat ∉ [1,80] pour (${a},${b},${n})`);
}

const pickPoolB = () => {
    const [a, b, n, res] = randomChoice(POOL_B);
    return { a, b, n, res };
};

// ═══════════════════════════════════════════════════════════
// POOL C — Résultat décimal fini non-entier (génération dynamique)
// ═══════════════════════════════════════════════════════════

/**
 * Génère dynamiquement (a, b, n) tel que (a × n) / b soit décimal fini non-entier.
 * - b ∈ {2, 4, 5, 8, 10, 16, 20, 25, 32, 40, 50} : seuls facteurs 2 et 5 → décimal fini
 * - (a × n) % b ≠ 0 : résultat non-entier
 * - (a × n) / b < 20 : résultat lisible 6ème
 * ~1000 combinaisons possibles.
 */
const pickPoolC = () => {
    const denomsOK = [2, 4, 5, 8, 10, 16, 20, 25, 32, 40, 50];
    for (let attempts = 0; attempts < 30; attempts++) {
        const b = randomChoice(denomsOK);
        const a = randInt(1, Math.min(2 * b, 20));
        const n = randInt(2, 10);
        if ((a * n) % b === 0) continue;  // éviter résultat entier
        if ((a * n) / b >= 20) continue;  // résultat < 20
        return { a, b, n };
    }
    // Fallback ultime (quasi jamais atteint) : cas canonique 3/4 × 2 = 1,5
    return { a: 3, b: 4, n: 2 };
};

// ═══════════════════════════════════════════════════════════
// POOL D — Comparaison à n (sous-famille D)
// ═══════════════════════════════════════════════════════════

/**
 * Cas de comparaison GÉNÉRÉS PAR CONSTRUCTION (enrichissement 2026-07-04,
 * remplace le pool de 20 couples figés). Invariants de la fiche conservés :
 *  - n multiple de b (n = b × q) → résultat a × q ENTIER par construction ;
 *  - propre : a < b, pgcd(a, b) = 1 → produit < n ;
 *  - impropre : b < a ≤ 2b + 2, pgcd(a, b) = 1 → produit > n
 *    (fraction irréductible, jamais entière) ;
 *  - grandeurs bornées comme dans le pool : n ≤ 80, résultat ≤ 100.
 */
const genCasComparaison = (type) => {
    for (let i = 0; i < 50; i++) {
        const b = type === 'propre' ? randInt(2, 9) : randInt(2, 6);
        const a = type === 'propre' ? randInt(1, b - 1) : randInt(b + 1, 2 * b + 2);
        if (gcd(a, b) !== 1) continue;
        const qMax = Math.min(Math.floor(80 / b), Math.floor(100 / a));
        if (qMax < 2) continue;
        const q = randInt(2, qMax);
        return { a, b, n: b * q, type, res: a * q };
    }
    // Repli déterministe (entrées historiques du pool, vérifiées)
    return type === 'propre'
        ? { a: 2, b: 5, n: 75, type, res: 30 }
        : { a: 5, b: 2, n: 8, type, res: 20 };
};

const pickPoolD = () => genCasComparaison(randomChoice(['propre', 'impropre']));

/** Sélection garantissant une parité propre/impropre à parts égales sur la longueur. */
const pickPoolDByType = (type) => genCasComparaison(type);

// ═══════════════════════════════════════════════════════════
// POOL E — Astucieux (n divisible par b, pour exercice 6)
// ═══════════════════════════════════════════════════════════

/**
 * Couples [a, b, n] où n est multiple de b, la méthode astucieuse est
 * « n/b puis × a ». On filtre POOL_B pour ne garder que ces couples.
 */
const POOL_E_ASTUCE = POOL_B.filter(([, b, n]) => n % b === 0);

for (const [a, b, n] of POOL_E_ASTUCE) {
    if (n % b !== 0) throw new Error(`POOL_E_ASTUCE: n non multiple de b pour (${a},${b},${n})`);
}

const pickPoolEAstuce = () => {
    const [a, b, n, res] = randomChoice(POOL_E_ASTUCE);
    return { a, b, n, res };
};

// ═══════════════════════════════════════════════════════════
// HELPERS DISTRACTEURS
// ═══════════════════════════════════════════════════════════

/** Garantit 4 choix distincts, ajoute des fallbacks numériques si doublon. */
const ensureFourDistinct = (bonne, distracteurs, fallbackFn) => {
    const set = new Set([bonne, ...distracteurs]);
    let k = 1;
    while (set.size < 4) {
        set.add(fallbackFn(k));
        k++;
        if (k > 50) throw new Error('ensureFourDistinct: fallback épuisé');
    }
    return [...set].slice(0, 4);
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — produit_fraction_entier_direct (QCM et Saisie)
// ═══════════════════════════════════════════════════════════

const genProduitDirectQcm = () => {
    const { a, b, n } = pickPoolA();
    const numRes = a * n;
    const simp = simplify(numRes, b);
    const bonne = fmtItem(simp.num, simp.den);
    // Distracteurs
    const distr_deuxEtages = fmtItem(numRes, b * n);              // (a×n)/(b×n)
    const distr_confusionPlus = `$${n} + ${fmtKatex(a, b)}$`;     // « a + n/b » forme KaTeX
    const distr_nonSimplifie = simp.den === b ? fmtItem(numRes + 1, b) : fmtItem(numRes, b); // oubli simp
    const choix = ensureFourDistinct(bonne, [distr_deuxEtages, distr_confusionPlus, distr_nonSimplifie],
        (k) => fmtItem(numRes + k, b));
    const texte = `<p>Calcule et donne le résultat sous forme de fraction la plus simple.</p>{{element:0}}`;
    const elementsAdditionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}} \\times ${n} = ?` } }],
    };
    const payload = makeQcm(choix, bonne, texte, opts(L_DIRECT));
    payload.elements_additionnels = elementsAdditionnels;
    payload._meta.generatorKey = 'produit_fraction_entier_direct';
    return payload;
};

const genProduitDirectSaisie = () => {
    const { a, b, n } = pickPoolA();
    const numRes = a * n;
    const simp = simplify(numRes, b);
    const bonne = fmtFrac(simp.num, simp.den);
    const texte = `<p>Calcule et donne le résultat sous forme de fraction la plus simple.</p>{{element:0}}`;
    const elementsAdditionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}} \\times ${n} = ?` } }],
    };
    const payload = makeSaisie(bonne, texte, { ...opts(L_DIRECT), strict: false });
    payload.elements_additionnels = elementsAdditionnels;
    payload._meta.generatorKey = 'produit_fraction_entier_direct';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — produit_resultat_entier (QCM et Saisie)
// ═══════════════════════════════════════════════════════════

const genProduitEntierQcm = () => {
    const { a, b, n, res } = pickPoolB();
    const bonne = String(res);
    // Distracteurs
    const numRes = a * n;
    const distr_fraction = fmtItem(numRes, b);                  // incomplétude : non simplifié
    const distr_plusMoins1 = String(res + (res > 1 ? -1 : 1)); // erreur de calcul ±1
    const distr_decimal = `${res},5`;                          // confusion entier/décimal
    const choix = ensureFourDistinct(bonne, [distr_fraction, distr_plusMoins1, distr_decimal],
        (k) => String(res + k + 1));
    const texte = `<p>Calcule et écris le résultat sous la forme d'un nombre entier.</p>{{element:0}}`;
    const elementsAdditionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}} \\times ${n} = ?` } }],
    };
    const payload = makeQcm(choix, bonne, texte, opts(L_ENTIER));
    payload.elements_additionnels = elementsAdditionnels;
    payload._meta.generatorKey = 'produit_resultat_entier';
    return payload;
};

const genProduitEntierSaisie = () => {
    const { a, b, n, res } = pickPoolB();
    const bonne = String(res);
    const texte = `<p>Calcule et écris le résultat sous la forme d'un nombre entier.</p>{{element:0}}`;
    const elementsAdditionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}} \\times ${n} = ?` } }],
    };
    const payload = makeSaisie(bonne, texte, { ...opts(L_ENTIER), strict: false });
    payload.elements_additionnels = elementsAdditionnels;
    payload._meta.generatorKey = 'produit_resultat_entier';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — produit_resultat_decimal (Saisie)
// ═══════════════════════════════════════════════════════════

const genProduitDecimalSaisie = () => {
    const { a, b, n } = pickPoolC();
    const bonne = fractionToDecimalExact(a * n, b);
    if (bonne === null) throw new Error(`genProduitDecimalSaisie: décimal infini pour (${a},${b},${n})`);
    const texte = `<p>Calcule et écris le résultat sous la forme d'un nombre décimal.</p>{{element:0}}`;
    const elementsAdditionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}} \\times ${n} = ?` } }],
    };
    const payload = makeSaisie(bonne, texte, { ...opts(L_DECIMAL), strict: false });
    payload.elements_additionnels = elementsAdditionnels;
    payload._meta.generatorKey = 'produit_resultat_decimal';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 4 — produit_egalite_trou (Texte à trous saisie)
// ═══════════════════════════════════════════════════════════

/**
 * Génère deux égalités indépendantes à trou :
 *   (trou gauche) k1 × a1/b1 = (k1×a1)/b1
 *   (trou droit)  a2/b2 × k2 = (a2×k2)/b2
 * Les deux entiers k1 et k2 doivent être DISTINCTS pour éviter la confusion visuelle.
 */
const pickTrouCouples = () => {
    // Pour garantir que k1 et k2 soient distincts et raisonnables (2..9)
    // et que a/b soit une fraction simplifiée (pgcd(a,b)=1).
    for (let attempt = 0; attempt < 100; attempt++) {
        const b1 = randInt(3, 9);
        const b2 = randInt(3, 9);
        if (b1 === b2) continue;
        const a1 = randInt(1, b1 - 1);
        const a2 = randInt(1, b2 - 1);
        if (gcd(a1, b1) !== 1) continue;
        if (gcd(a2, b2) !== 1) continue;
        const k1 = randInt(3, 9);
        const k2 = randInt(3, 9);
        if (k1 === k2) continue;
        const c1 = k1 * a1;
        const c2 = a2 * k2;
        if (c1 > 99 || c2 > 99) continue;
        return { a1, b1, k1, c1, a2, b2, k2, c2 };
    }
    // Fallback fixe
    return { a1: 8, b1: 7, k1: 7, c1: 56, a2: 7, b2: 5, k2: 6, c2: 42 };
};

const genProduitEgaliteTrou = () => {
    const { a1, b1, k1, c1, a2, b2, k2, c2 } = pickTrouCouples();
    const phrase = `$` + `{{${k1}}} \\times ${fmtKatex(a1, b1)} = ${fmtKatex(c1, b1)} \\quad\\text{et}\\quad ${fmtKatex(a2, b2)} \\times {{${k2}}} = ${fmtKatex(c2, b2)}$`;
    const texte = `<p>Complète l'égalité en tapant le nombre qui convient.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_TROU), mode: 'saisie', strict: false });
    payload._meta.generatorKey = 'produit_egalite_trou';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 5 — produit_comparer_a_entier (QCM)
// ═══════════════════════════════════════════════════════════

const genProduitComparerQcm = () => {
    // 50/50 propre vs impropre
    const type = randomChoice(['propre', 'impropre']);
    const { a, b, n } = pickPoolDByType(type);
    let bonne, distr_inverse;
    if (type === 'impropre') {
        bonne = `Supérieur à ${n} (car ${fmtItem(a, b)} > 1)`;
        distr_inverse = `Inférieur à ${n} (car ${a} > ${b})`;
    } else {
        bonne = `Inférieur à ${n} (car ${fmtItem(a, b)} < 1)`;
        distr_inverse = `Supérieur à ${n} (car ${a} < ${b})`;
    }
    const distr_egal = `Égal à ${n}`;
    const distr_inconnu = `On ne peut pas savoir sans calculer la valeur exacte`;
    const choix = [bonne, distr_inverse, distr_egal, distr_inconnu];
    // Produit en KaTeX dans l'énoncé (audit fractions en texte, 2026-09-06).
    const texte = `<p>On calcule ${tex(`\\dfrac{${a}}{${b}} \\times ${n}`)}. Le résultat est-il supérieur, inférieur ou égal à ${n} ?</p>{{element:0}}`;
    const elementsAdditionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}} \\times ${n} \\;\\text{vs}\\; ${n}` } }],
    };
    const payload = makeQcm(choix, bonne, texte, opts(L_COMPARE));
    payload.elements_additionnels = elementsAdditionnels;
    payload._meta.generatorKey = 'produit_comparer_a_entier';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 6 — produit_astucieux (QCM)
// ═══════════════════════════════════════════════════════════

const genProduitAstucieuxQcm = () => {
    const { a, b, n, res } = pickPoolEAstuce();
    // Méthode astucieuse : diviser n par b, puis multiplier par a
    const nSurB = n / b;
    // Méthode naïve (correcte mais lourde) : multiplier a par n, puis diviser par b
    const aFoisN = a * n;
    // Méthode décimale : convertir a/b en décimal, puis multiplier par n (souvent impossible)
    const bonne = `Je divise d'abord ${n} par ${b}, j'obtiens ${nSurB}, puis je multiplie par ${a} : ${a} × ${nSurB} = ${res}.`;
    const distr_naive = `Je multiplie d'abord ${a} par ${n}, j'obtiens ${aFoisN}, puis je divise par ${b} : ${aFoisN} ÷ ${b} = ${res}.`;
    const decStr = fractionToDecimalExact(a, b);
    const distr_decimal = decStr !== null
        ? `Je convertis ${fmtItem(a, b)} en décimal (${decStr}), puis je multiplie ${decStr} par ${n} : ${decStr} × ${n} = ${res}.`
        : `Je convertis ${fmtItem(a, b)} en décimal, puis je multiplie par ${n}.`;
    const distr_calculatrice = `Je ne peux pas calculer ce produit mentalement, il faut une calculatrice.`;
    const choix = [bonne, distr_naive, distr_decimal, distr_calculatrice];
    const texte = `<p>Quelle méthode est la plus astucieuse pour calculer ce produit mentalement ?</p>{{element:0}}`;
    const elementsAdditionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}} \\times ${n}` } }],
    };
    const payload = makeQcm(choix, bonne, texte, opts(L_ASTUCE));
    payload.elements_additionnels = elementsAdditionnels;
    payload._meta.generatorKey = 'produit_astucieux';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY DES GÉNÉRATEURS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    produit_fraction_entier_direct_qcm_unique: genProduitDirectQcm,
    produit_fraction_entier_direct_texte:      genProduitDirectSaisie,
    produit_resultat_entier_qcm_unique:        genProduitEntierQcm,
    produit_resultat_entier_texte:             genProduitEntierSaisie,
    produit_resultat_decimal_texte:            genProduitDecimalSaisie,
    produit_egalite_trou_texte_trou_saisie:    genProduitEgaliteTrou,
    produit_comparer_a_entier_qcm_unique:      genProduitComparerQcm,
    produit_astucieux_qcm_unique:              genProduitAstucieuxQcm,
};

// ═══════════════════════════════════════════════════════════
// CATEGORIES (pour l'UI prof)
// ═══════════════════════════════════════════════════════════

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    qcm_multiple: 'QCM Multi',
    texte: 'Saisie',
    texte_trou_drag: 'Drag',
    texte_trou_saisie: 'Saisie trous',
    relier: 'Relier',
    tableau_vf: 'Vrai/Faux',
    classer: 'Classer',
    ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter(k => k === prefix || k.startsWith(`${prefix}_`))
        .map(k => {
            const suffix = k.slice(prefix.length + 1);
            return { key: k, label: FORMAT_LABELS[suffix] || suffix || 'Standard' };
        });

export const CATEGORIES = [
    { id: 'produit_fraction_entier_direct', label: 'Produit : résultat fraction',  formatsList: buildFormats('produit_fraction_entier_direct') },
    { id: 'produit_resultat_entier',        label: 'Produit : résultat entier',    formatsList: buildFormats('produit_resultat_entier') },
    { id: 'produit_resultat_decimal',       label: 'Produit : résultat décimal',   formatsList: buildFormats('produit_resultat_decimal') },
    { id: 'produit_egalite_trou',           label: 'Égalité à trou',               formatsList: buildFormats('produit_egalite_trou') },
    { id: 'produit_comparer_a_entier',      label: 'Comparer à l\'entier',         formatsList: buildFormats('produit_comparer_a_entier') },
    { id: 'produit_astucieux',              label: 'Méthode astucieuse',           formatsList: buildFormats('produit_astucieux') },
];

// Filtre anti-collision de préfixes (au cas où)
for (const cat of CATEGORIES) {
    cat.formatsList = cat.formatsList.filter(f => {
        for (const other of CATEGORIES) {
            if (other.id === cat.id) continue;
            if (other.id.startsWith(cat.id + '_') && (f.key === other.id || f.key.startsWith(other.id + '_'))) {
                return false;
            }
        }
        return true;
    });
}

// ═══════════════════════════════════════════════════════════
// ENTRÉE PRINCIPALE
// ═══════════════════════════════════════════════════════════

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Toujours forcer la clé complète (les générateurs ne posent que le préfixe
    // catégorie ce qui cassait le "regenerate dans le même format")
    payload._meta.generatorKey = key;
    return payload;
};

// Exports internes pour les tests
export const _internals = {
    POOL_A, POOL_B, POOL_E_ASTUCE,
    GENERATORS, CATEGORIES,
    gcd, simplify, fmtFrac, fmtKatex, fmtItem, fmtResult, isInteger, fractionToDecimalExact,
    pickPoolA, pickPoolB, pickPoolC, pickPoolD, pickPoolDByType, pickPoolEAstuce,
    pickTrouCouples, ensureFourDistinct,
    genProduitDirectQcm, genProduitDirectSaisie,
    genProduitEntierQcm, genProduitEntierSaisie,
    genProduitDecimalSaisie,
    genProduitEgaliteTrou,
    genProduitComparerQcm,
    genProduitAstucieuxQcm,
};
